// frontend/src/pages/AccesoHandoff.jsx
// Aterrizaje del login unificado: canjea el código de un solo uso que llega
// desde el sitio comercial (www.fleetcontrolcloud.com) por el JWT del tenant
// y entra directo al sistema, sin re-tipear credenciales.
import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { inicioDePortal } from '../utils/portales';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.xxl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  width: 100%;
  max-width: 420px;
  text-align: center;
  animation: ${fadeIn} 0.4s ease;
`;

const AppTitle = styled.h1`
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xxl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0 0 ${({ theme }) => theme.spacing.md};
`;

const Mensaje = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
`;

const ErrorBox = styled.div`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  background: ${({ theme }) => theme.colors.dangerAlpha};
  border: 1px solid ${({ theme }) => theme.colors.dangerBorder};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const VolverButton = styled.button`
  padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.lg}`};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover { background: ${({ theme }) => theme.colors.primaryHover}; }
`;

const AccesoHandoff = () => {
  const navigate = useNavigate();
  const { loginConCodigo } = useAuth();
  const [errorMsg, setErrorMsg] = useState('');
  // Evita canjear dos veces el mismo código (doble render de StrictMode): el
  // código es de un solo uso, el segundo intento fallaría siempre.
  const yaCanjeado = useRef(false);

  useEffect(() => {
    if (yaCanjeado.current) return;
    yaCanjeado.current = true;

    const code = new URLSearchParams(window.location.search).get('code');
    if (!code) {
      setErrorMsg('Enlace de acceso inválido. Volvé a iniciar sesión.');
      return;
    }

    (async () => {
      const resultado = await loginConCodigo(code);
      if (resultado.success) {
        navigate(inicioDePortal(resultado.user?.rol), { replace: true });
      } else {
        setErrorMsg(resultado.error || 'No se pudo completar el acceso. Volvé a iniciar sesión.');
      }
    })();
  }, [loginConCodigo, navigate]);

  return (
    <Container>
      <Card>
        <AppTitle>FC Cloud</AppTitle>
        {errorMsg ? (
          <>
            <ErrorBox>{errorMsg}</ErrorBox>
            <VolverButton type="button" onClick={() => navigate('/login', { replace: true })}>
              Ir al inicio de sesión
            </VolverButton>
          </>
        ) : (
          <Mensaje>Ingresando a tu sistema…</Mensaje>
        )}
      </Card>
    </Container>
  );
};

export default AccesoHandoff;
